import {
  ProjektdokumentationChecklistItem,
  ProjektdokumentationSection,
} from "../types/projektdokumentation";

export const VOT_PHOTO_DIRECTORIES = ["Bilder", "VOT"] as const;
export const VOT_PHOTO_MAX_DIMENSION = 2000;
export const VOT_PHOTO_JPEG_QUALITY = 0.82;
const PHOTO_DISABLED_SECTION_IDS = new Set(["vollstaendigkeit-upload"]);
const PHOTO_DISABLED_SECTION_CODES = new Set(["n"]);

const SECTION_SLUGS: Record<string, string> = {
  "heizlast-verbrauch": "heizlast-verbrauch",
  "fotos-aussen": "fotos-aussen",
  "fotos-innen": "fotos-innen",
  "leitungswege-kaeltemittel": "kaeltemittel",
  "leitungswege-elektro": "elektro-weg",
  "zuweg-einbringung": "zuweg-einbringung",
  bestandsanlage: "bestandsanlage",
  heizsystem: "heizsystem",
  elektro: "elektro",
  abwasser: "abwasser",
  rohrdimensionen: "rohrdimensionen",
  "technische-angaben": "technische-angaben",
  "leitungsfuehrung-umsetzung": "leitungsfuehrung",
  kundenabsprachen: "kundenabsprachen",
  "vollstaendigkeit-upload": "vollstaendigkeit-upload",
};

const ITEM_SLUGS: Record<string, string> = {
  "heating-load-calculation-available": "heizlastberechnung",
  "building-exterior-view": "aussenansicht-gebaeude",
  "outdoor-unit-location-documented": "aufstellort-ausseneinheit",
  "planned-position-marked": "position-eingezeichnet",
  "core-drilling-outside-marked": "kernbohrung-aussen",
  "heating-room-overview": "gesamtuebersicht-heizraum",
  "indoor-unit-location-documented": "aufstellort-inneneinheit",
  "core-drilling-inside-marked": "kernbohrung-innen",
  "movement-area-documented": "bewegungsflaechen",
  "refrigerant-route-documented": "kaeltemittel-verlauf",
  "refrigerant-route-media": "kaeltemittel-fotos-video",
  "refrigerant-route-marked": "kaeltemittel-verlauf-eingezeichnet",
  "electrical-route-documented": "elektro-verlauf",
  "electrical-route-media": "elektro-fotos-video",
  "electrical-route-obstacles-visible": "elektro-hindernisse",
  "building-access-documented": "zugang-gebaeude",
  "heating-room-access-documented": "zugang-heizraum",
  "tightest-access-recorded": "engste-stelle",
  "existing-system-overview": "gesamtansicht-heizungsanlage",
  "typenschild-altanlage-dokumentiert": "typenschild-altanlage",
  "connection-areas-visible": "anschlussbereiche",
  "hydraulics-visible": "hydraulik",
  "radiators-documented": "heizkoerper",
  "radiator-valves-documented": "heizkoerperventile",
  "underfloor-heating-documented": "fussbodenheizung",
  "room-thermostats-documented": "raumthermostate",
  "meter-cabinet-opened": "zaehlerschrank",
  "service-connection-box-documented": "hausanschlusskasten",
  "drain-availability-recorded": "abfluss-vorhanden",
  "drain-documented": "abfluss-dokumentiert",
  "main-connections-photographed": "hauptanschluesse",
  "measurement-reference-visible": "zollstock-referenz",
  "pipe-dimensions-accessible": "rohrdimensionen-zugaenglich",
  "heating-room-area-recorded": "heizraum-flaeche",
  "tightest-access-confirmed": "engste-stelle",
  "low-ceiling-height-present": "niedrige-deckenhoehe",
  "low-ceiling-height-documented": "niedrige-deckenhoehe-dokumentiert",
  "pv-status-recorded": "pv-status",
  "core-drilling-fixed": "kernbohrung-festgelegt",
  "route-understandable": "leitungsweg-nachvollziehbar",
  "positions-defined": "aufstellorte",
  "customer-preferences-documented": "kundenwuensche",
  "customer-contributions-documented": "eigenleistungen",
  "special-challenges-documented": "besondere-herausforderungen",
  "special-challenges-structural": "baulich",
  "special-challenges-technical": "technisch",
  "special-challenges-organizational": "organisatorisch",
  "technical-questions-status": "offene-technische-fragen",
  "all-media-uploaded": "alle-medien-hochgeladen",
  "structured-storage-available": "strukturierte-ablage",
  "documentation-complete": "dokumentation-vollstaendig",
};

const normalizeSlug = (value: string): string =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "") || "punkt";

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function getVotPhotoPrefixFromIds(
  sectionId: string,
  sectionCode: string,
  itemId: string
): string {
  const code = normalizeSlug(sectionCode);
  const sectionSlug = SECTION_SLUGS[sectionId] || normalizeSlug(sectionId);
  const itemSlug = ITEM_SLUGS[itemId] || normalizeSlug(itemId);
  return `${code}-${sectionSlug}-${itemSlug}-`;
}

export function getVotSectionPhotoPrefixFromIds(
  sectionId: string,
  sectionCode: string
): string {
  const code = normalizeSlug(sectionCode);
  const sectionSlug = SECTION_SLUGS[sectionId] || normalizeSlug(sectionId);
  return `${code}-${sectionSlug}-`;
}

export function isVotPhotoSectionSupported(sectionId: string, sectionCode: string): boolean {
  return (
    !PHOTO_DISABLED_SECTION_IDS.has(sectionId) &&
    !PHOTO_DISABLED_SECTION_CODES.has(normalizeSlug(sectionCode))
  );
}

export function getVotPhotoPrefix(
  section: ProjektdokumentationSection,
  item: ProjektdokumentationChecklistItem
): string {
  return getVotPhotoPrefixFromIds(section.id, section.code, item.id);
}

export function getVotSectionPhotoPrefix(section: ProjektdokumentationSection): string {
  return getVotSectionPhotoPrefixFromIds(section.id, section.code);
}

export function extractVotPhotoSequence(prefix: string, fileName: string): number | null {
  // Ticket #1055 (Nutzer-Rückmeldung): eine markierte Version heißt "<prefix><nr>-markiert.jpg"
  // (withMarkedFileNameSuffix hängt den Suffix vor der Endung an) — ohne das optionale
  // "-markiert" hier würde dieses Regex nicht matchen, die Markierung fiele aus dem Filter in
  // `votDriveFiles.filter((file) => isVotPhotoFileNameForPrefix(...))` heraus und wäre für
  // pairVotPhotos unsichtbar (Original würde trotz gespeicherter Markierung weiter angezeigt,
  // inkl. fehlender Beschriftung, da die markierte Datei mit der Caption gar nicht erst gerendert
  // wurde).
  const match = fileName.match(new RegExp(`^${escapeRegExp(prefix)}(\\d+)(?:-markiert)?\\.jpe?g$`, "i"));
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
}

export function isVotPhotoFileNameForPrefix(prefix: string, fileName: string): boolean {
  return extractVotPhotoSequence(prefix, fileName) !== null;
}

export function getNextVotPhotoFileNames(
  prefix: string,
  existingFileNames: string[],
  count: number
): string[] {
  const maxExisting = existingFileNames.reduce((max, fileName) => {
    const sequence = extractVotPhotoSequence(prefix, fileName);
    return sequence && sequence > max ? sequence : max;
  }, 0);

  return Array.from({ length: count }, (_, index) => {
    const sequence = String(maxExisting + index + 1).padStart(2, "0");
    return `${prefix}${sequence}.jpg`;
  });
}
